/** Table, filter, and endorsement config for Professional Guardian bonds view (Phase 9.13F). */

import {
  DWB_LABEL_MAP,
  getReleaseRiderUrl as getSharedReleaseRiderUrl,
  isNameChangeEndorsement,
} from '../hooks/bondsEndorsementSharedConfig';

export const PG_BONDS_STATUS_OPTIONS = ['Active', 'Rejected', 'To Be Cancelled', 'Cancelled'];

export { isNameChangeEndorsement };
export const PG_BONDS_DWB_LABEL_MAP = DWB_LABEL_MAP;

export const PG_BONDS_BOND_TYPE_TO_SUBCATEGORIES = {
  Probate: [
    'Administrator',
    'Conservator',
    'Curator',
    'Executor',
    'Personal Representative',
  ],
  Guardianship: [
    'Guardian - Incapacitated',
    'Guardian - Minor',
    'Guardian - Advocate',
    'Conservator',
  ],
  Blanket: ['1 Year Bond', '2 Year Bond', '3 Year Bond'],
};

export const PG_BONDS_ENDORSEMENT_TYPE_OPTIONS = [
  { value: 'Increase', label: 'Increase' },
  { value: 'Decrease', label: 'Decrease' },
  { value: 'Name_Change_Principal', label: 'Name change (Principal)' },
  { value: 'Name_Change_County', label: 'Name change (County)' },
  { value: 'Name_Change_Deceased_Ward_Beneficiary', label: 'Name change (Deceased/Ward/Beneficiary)' },
];

export const PG_BONDS_VALID_DOCUMENT_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

export const PG_BONDS_DEFAULT_FILTERS = {
  bondNumber: [],
  bondType: [],
  bondSubCategory: [],
  principal: [],
  estate: [],
  lawFirm: [],
  attorney: [],
  internalUser: [],
  status: [],
  startDate: null,
  endDate: null,
};

export function createEmptyPgBondsFilters() {
  return {
    bondNumber: [],
    bondType: [],
    bondSubCategory: [],
    principal: [],
    estate: [],
    lawFirm: [],
    attorney: [],
    internalUser: [],
    status: [],
    startDate: null,
    endDate: null,
  };
}

export function getPgBondsTableColumns(role) {
  return [
    { key: 'bond_number', label: 'Bond Number' },
    { key: 'bond_type', label: 'Bond Type' },
    { key: 'bond_sub_category', label: 'Bond Sub Category' },
    { key: 'principals', label: 'Principals' },
    { key: 'estate_name', label: 'Estate Name' },
    { key: 'law_firm_name', label: 'Law Firm' },
    { key: 'attorney_name', label: 'Attorney' },
    ...(role === 'internal_user' || role === 'super_user'
      ? [{ key: 'managed_by', label: 'Managed By' }]
      : []),
    { key: 'bond_amount', label: 'Bond Amount' },
    { key: 'status', label: 'Status' },
    { key: 'created_at', label: 'Created At' },
  ];
}

export function getBondUpdateUrl(bond) {
  if (bond.bond_type === 'VA') return `/vermost-va-bond-update/${bond.bond_id}`;
  if (bond.bond_type === 'Trustee') return `/vermost-trustee-bond-update/${bond.bond_id}`;
  if (bond.bond_type === 'Probate') return `/vermost-probate-bond-update/${bond.bond_id}`;
  if (bond.bond_type === 'Blanket') return `/vermost-blanket-bond-update/${bond.bond_id}`;
  return `/vermost-guardianship-bond-update/${bond.bond_id}`;
}

export function getReleaseRiderUrl(bond) {
  return getSharedReleaseRiderUrl(bond);
}

export function formatPgBondAmount(amount) {
  if (amount === null) return '$0.00';
  return `$${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

export function transformPgBondStatusForDisplay(status) {
  return status === 'Approved' ? 'Active' : status;
}

export function isInternalOrSuperRole(role) {
  return role === 'internal_user' || role === 'super_user';
}

export function isNameChangeValidationOk({
  endorsementType,
  endorsementToCounty,
  updatedName,
  bondType,
  dwbFirstName,
  dwbLastName,
}) {
  if (endorsementType === 'Name_Change_County') {
    return !!endorsementToCounty;
  }
  if (endorsementType === 'Name_Change_Deceased_Ward_Beneficiary') {
    const labels = PG_BONDS_DWB_LABEL_MAP[bondType];
    if (labels?.single) return !!updatedName;
    return !!(dwbFirstName || dwbLastName);
  }
  if (endorsementType === 'Name_Change_Principal') {
    return !!updatedName;
  }
  return true;
}

export function isEndorsementSubmitDisabled({
  isSubmittingEndorsement,
  endorsementType,
  endorsementFile,
  endorsementToCounty,
  selectedBond,
  updatedName,
  dwbFirstName,
  dwbLastName,
}) {
  if (isSubmittingEndorsement) return true;
  if (!isNameChangeEndorsement(endorsementType) && !endorsementFile) return true;
  if (endorsementType === 'Name_Change_County' && !endorsementToCounty) return true;
  if (endorsementType === 'Name_Change_Deceased_Ward_Beneficiary') {
    const labels = PG_BONDS_DWB_LABEL_MAP[selectedBond?.bond_type];
    if (labels?.single) return !updatedName;
    return !(dwbFirstName || dwbLastName);
  }
  return false;
}
